'use client';

import Link from 'next/link';

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import type { UseFormReturn } from 'react-hook-form';

import { register } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, number as __eliadmin_zod_2, boolean as __eliadmin_zod_3 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { Paragraph } from '../ui/paragraph';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { useQuery } from '@tanstack/react-query';
import { Heading } from '../ui/heading';
import { RegisterSuccess } from './register-success';

import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { Field } from '@eliadmin/core/src/components/form/field';
import { MdDomain, MdLockOutline, MdOutlineEmail } from 'react-icons/md';
import { RiAccountCircleLine } from 'react-icons/ri';
import { DialogClose } from '@eliadmin/core/src/components/ui/dialog';
import { LuX } from 'react-icons/lu';

function Separator({ className }: { className?: string }) {
  return <div className={cn('h-[1px] w-full bg-border', className)} />;
}

export type RegisterFormValues = z.infer<ReturnType<typeof registerSchema>>;

export const registerSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
      name: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      password: __eliadmin_zod_1()
        .min(6, VM.MIN_LENGTH_VALIDATION_MESSAGE(6))
        .max(64, VM.MAX_LENGTH_VALIDATION_MESSAGE(64))
        .regex(/[A-Z]/)
        .regex(/[0-9]/),
      password_confirmation: __eliadmin_zod_1().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),

      address: __eliadmin_zod_1().optional(),
      street_number: __eliadmin_zod_1().optional(),
      postcode: __eliadmin_zod_1().optional(),
      city: __eliadmin_zod_1().optional(),
      country: __eliadmin_zod_0({
          id: __eliadmin_zod_1().or(__eliadmin_zod_2()).optional(),
        })
        .optional(),

      company_buy: __eliadmin_zod_3().optional(),
      company: __eliadmin_zod_1().optional(),
      ico: __eliadmin_zod_1().optional(),
      dic: __eliadmin_zod_1().optional(),
      ic_dph: __eliadmin_zod_1().optional(),
      email_company: __eliadmin_zod_1().optional(),
      address_company: __eliadmin_zod_1().optional(),
      street_number_company: __eliadmin_zod_1().optional(),
      postcode_company: __eliadmin_zod_1().optional(),
      city_company: __eliadmin_zod_1().optional(),
      country_company: __eliadmin_zod_0({
          id: __eliadmin_zod_1(),
        })
        .optional(),

      gdpr: __eliadmin_zod_3().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
      newsletter: __eliadmin_zod_3().optional(),

      newsletter_return_url: __eliadmin_zod_1(),
      tos: __eliadmin_zod_3().refine(val => val === true, { message: VM.TOS_VALIDATION_MESSAGE() }),
      'g-recaptcha-response': __eliadmin_zod_1().default(''),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

export function RegisterForm({
  onShowSignIn,
  inDialog = false,
  onSuccess,
}: {
  onShowSignIn?: () => void;
  inDialog?: boolean;
  onSuccess?: () => void;
}) {
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);

  const { formValidationMessages } = useFormValidationMessages(t);

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const form = useForm<z.infer<ReturnType<typeof registerSchema>>>({
    resolver: zodResolver(registerSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      gdpr: false,
      newsletter: false,
      tos: false,

      address: '',
      street_number: '',
      postcode: '',
      city: '',
      country: {
        id: 'SK',
      },

      company_buy: false,
      company: '',
      ico: '',
      dic: '',
      ic_dph: '',
      email_company: '',
      address_company: '',
      street_number_company: '',
      postcode_company: '',
      city_company: '',
      country_company: undefined,

      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
    },
  });

  const router = useRouter();

  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = form.handleSubmit(async data => {
    if (data.password !== data.password_confirmation) {
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    try {

      const resp = await register({ ...data,  locale });

      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });

      if (inDialog) {
        if (onSuccess) onSuccess();
        else setIsSuccess(true);
      }

      if (!inDialog) {
        toast({
          variant: 'success',
          title: 'Úspešne ste sa zaregistrovali',
        });
        router.push(ROUTES.PROFILE());
      }
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
      }
    }
  });

  if (isSuccess) {
    return <RegisterSuccess inDialog={inDialog} />;
  }

  return (
    <Form {...form}>
      <form
        className={cn(
          'flex w-full flex-col items-center justify-center gap-6',
          'ea-register-form',
          !inDialog && 'py-8 sm:py-12 lg:py-14',
        )}
        onSubmit={handleSubmit}
      >
        <div className="flex w-full flex-col items-center justify-center gap-6">
          <div className="relative flex w-full items-center justify-center">
            <Heading title={t('text758')} className={{ container: 'text-center' }} tag={'h3'} />
            {inDialog && (
              <DialogClose className="absolute right-0 top-1/2 -translate-y-1/2">
                <LuX size={24} className="text-[#D1D5DB]" />
              </DialogClose>
            )}
          </div>
          <Paragraph className="text-center" size="base" color="neutral">
            {t('text623')}&nbsp;
            <span
              className={cn(
                'cursor-pointer font-bold text-primary transition-all hover:opacity-70',
              )}
              onClick={inDialog ? onShowSignIn : () => router.push(ROUTES.SIGN_IN())}
            >
              {t('text624')}
            </span>
          </Paragraph>
        </div>

        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-6',
            'ea-register-form__content',
          )}
        >
          <div className="grid w-full grid-cols-12 gap-4">
            <FormTitle name={t('text385')} className="col-span-12" />
            <Field
              required
              type="email"
              name="email"
              placeholder={t('text3698')}
              label={t('text3698')}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full' }}
              before={<MdOutlineEmail size={20} className="text-[#9CA3AF]" />}
            />
            <Field
              required
              type="password"
              name="password"
              placeholder={`${t('text191')}`}
              label={`${t('text191')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
              before={<MdLockOutline size={20} className="text-[#9CA3AF]" />}
              description={t('text2349')}
            />
            <Field
              required
              type="password"
              name="password_confirmation"
              placeholder={`${t('text3183')}`}
              label={`${t('text3183')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
              before={<MdLockOutline size={20} className="text-[#9CA3AF]" />}
            />
          </div>

          <Separator />

          <div className="grid w-full grid-cols-12 gap-4">
            <FormTitle name={t('text3560')} className="col-span-12" />
            <FormField
              control={form.control}
              name="company_buy"
              render={({ field }) => (
                <FormItem
                  className={cn('ea-register-form__company-type-field', 'col-span-12 w-full')}
                >
                  <FormControl>
                    <div className="grid w-full grid-cols-2 gap-3">
                      <div
                        onClick={() => field.onChange(false)}
                        className={cn(
                          'col-span-1 flex w-full cursor-pointer flex-row items-center justify-start gap-3 rounded-[20px] p-4 text-sm font-medium tracking-tight sm:text-base',
                          field.value === false
                            ? 'border border-primary bg-primary text-white'
                            : 'border bg-transparent',
                        )}
                      >
                        <RiAccountCircleLine
                          size={24}
                          className={cn(field.value === false ? 'text-white' : 'text-primary')}
                        />
                        <p>{t('text2234')}</p>
                      </div>

                      <div
                        onClick={() => field.onChange(true)}
                        className={cn(
                          'col-span-1 flex w-full cursor-pointer flex-row items-center justify-start gap-3 rounded-[20px] p-4 text-sm font-medium tracking-tight sm:text-base',
                          field.value === true
                            ? 'border border-primary bg-primary text-white'
                            : 'border bg-transparent',
                        )}
                      >
                        <MdDomain
                          size={24}
                          className={cn(field.value === true ? 'text-white' : 'text-primary')}
                        />
                        <p>{t('text1432')}</p>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.watch('company_buy') === true ? (
              <CompanyFields
                countries={countries.data}
                isCountriesLoading={isCountriesLoading}
                form={form}
              />
            ) : (
              <PersonFields
                countries={countries.data}
                isCountriesLoading={isCountriesLoading}
                form={form}
              />
            )}
          </div>

          <Separator />

          <div className="col-span-12 grid w-full grid-cols-12 flex-col items-start justify-start gap-4 gap-x-3 gap-y-4">
            <FormTitle name={t('text803')} className="col-span-12" />
            {form.watch('company_buy') === true && (
              <>
                <Field
                  name="name"
                  label={`${t('text33')} *`}
                  placeholder={`${t('text33')}`}
                  control={form.control}
                  className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
                />
                <Field
                  name="surname"
                  label={`${t('text34')} *`}
                  placeholder={`${t('text34')}`}
                  control={form.control}
                  className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
                />
              </>
            )}

            {/* {form.watch('company_buy') === true && (
              <Field
                name="email_company"
                type="email"
                label={'Email *'}
                placeholder={'Email'}
                control={form.control}
                className={{ wrapper: 'col-span-12 w-full' }}
              />
            )} */}
            {/* <Field
              name="name"
              label={`${t('text33')} *`}
              placeholder={`${t('text33')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            />
            <Field
              name="surname"
              label={`${t('text34')} *`}
              placeholder={`${t('text34')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            /> */}
            <Field
              required
              name="phone"
              label={`${t('text1258')}`}
              placeholder={`${t('text1258')}`}
              control={form.control}
              className={{ wrapper: 'col-span-12 w-full' }}
              type="tel"
            />
            {/* {form.watch('company_buy') === true && (
              <>
                <Separator className="col-span-12 my-2 w-full" />
                <div className="col-span-12 grid w-full grid-cols-12 flex-col items-start justify-start gap-4 gap-x-2 gap-y-4 md:gap-x-3">
                  <FormTitle name="Kontaktná osoba" className="col-span-12" />
                </div>
              </>
            )} */}
          </div>
        </div>
        <Separator />
        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-4',
            'ea-register-form__footer',
          )}
        >
          <FormField
            control={form.control}
            name="tos"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-start gap-0',
                  'ea-register-form__tos-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="font-medium">
                  {t('text264')}{' '}
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.TOS?.(), locale, 'tos')}
                    className="font-bold text-primary"
                    prefetch={false}
                    target="_blank"
                  >
                    {t('text265')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-start gap-0',
                  'ea-register-form__gdpr-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(
                      ROUTES?.GDPR?.() ?? '/',
                      locale,
                      'gdpr',
                    )}
                    className="font-bold text-primary"
                    prefetch={false}
                    target="_blank"
                  >
                    {t('text364')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('hidden w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="mt-2 flex w-full items-center justify-center">
            <Button
              type="submit"
              className={cn('h-12 w-full sm:w-fit', 'ea-register-form__submit-button')}
              isLoading={form.formState.isSubmitting}
            >
              {t('text758')}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

function FormTitle({ name, className }: { name: string; className?: string }) {
  return (
    <Heading
      className={{
        title: 'text-start font-medium tracking-tighter',
        container: cn('col-span-full w-full items-start justify-start text-start', className),
      }}
      title={name}
      tag="h4"
    />
  );
}

function PersonFields({
  form,
  countries,
  isCountriesLoading,
}: {
  form: UseFormReturn<RegisterFormValues>;
  countries: Country[];
  isCountriesLoading: boolean;
}) {
  const { t } = useTranslations();

  return (
    <>
      <Field
        required
        name="name"
        placeholder={`${t('text33')}`}
        label={`${t('text33')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        required
        name="surname"
        placeholder={`${t('text34')}`}
        label={`${t('text34')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        required
        name="address"
        placeholder={`${t('text157')}`}
        label={`${t('text157')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        required
        name="street_number"
        placeholder={`${t('text438')}`}
        label={`${t('text438')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        required
        name="postcode"
        placeholder={`${t('text40')}`}
        label={`${t('text40')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-3' }}
      />
      <Field
        required
        name="city"
        placeholder={`${t('text39')}`}
        label={`${t('text39')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-9' }}
      />
      <Field
        required
        name="country.id"
        type="select"
        placeholder={t('text1410')}
        label={`${t('text959')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full' }}
        isDisabled={isCountriesLoading}
        options={countries.map(c => ({
          value: c.id,
          label: c.name,
        }))}
      />
    </>
  );
}

function CompanyFields({
  form,
  countries,
  isCountriesLoading,
}: {
  form: UseFormReturn<RegisterFormValues>;
  countries: Country[];
  isCountriesLoading: boolean;
}) {
  const { t } = useTranslations();

  return (
    <>
      <Field
        required
        name="company"
        placeholder={`${t('text1188')}`}
        label={`${t('text1188')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
      <Field
        required
        name="ico"
        placeholder={`${t('text42')}`}
        label={`${t('text42')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="dic"
        placeholder={`${t('text43')}`}
        label={`${t('text43')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
      />
      <Field
        name="ic_dph"
        placeholder={`${t('text44')}`}
        label={`${t('text44')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full' }}
      />
      <Field
        required
        name="address_company"
        placeholder={`${t('text38')}`}
        label={`${t('text38')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
      />
      <Field
        required
        name="street_number_company"
        placeholder={`${t('text438')}`}
        label={`${t('text438')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
      />
      <Field
        name="postcode_company"
        placeholder={`${t('text40')}`}
        label={`${t('text40')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-3' }}
      />
      <Field
        name="city_company"
        placeholder={`${t('text39')}`}
        label={`${t('text39')}`}
        control={form.control}
        className={{ wrapper: 'col-span-12 w-full sm:col-span-9' }}
      />
      <Field
        required
        control={form.control}
        type="select"
        name="country_company.id"
        label={`${t('text959')}`}
        placeholder={t('text1410')}
        className={{ wrapper: 'col-span-12 w-full' }}
        isDisabled={isCountriesLoading}
        options={countries.map(c => ({
          value: c.id,
          label: c.name,
        }))}
      />
    </>
  );
}
